import React, { useState, useEffect, useCallback, FormEvent, ChangeEvent } from 'react';
import { rateLimitMonitoringAPI, handleApiError } from '../services/api';
import './RateLimitMonitoring.css';

interface Stats {
  // Define based on actual API response
  [key: string]: unknown;
}

interface HealthDetails {
  redis_connected?: boolean;
  redis_responsive?: boolean;
  rate_limiting_active?: boolean;
}

interface Health {
  healthy?: boolean;
  details?: HealthDetails;
}

interface RateLimitConfig {
  registration?: string;
  login?: string;
  login_failed?: string;
  verification?: string;
  refresh?: string;
  password_change?: string;
  password_reset?: string;
  current_user?: string;
  logout?: string;
  emergency?: string;
  location_update?: string;
  burst_multiplier?: number;
  lockout_threshold?: number;
  lockout_duration?: number;
}

interface UserInfo {
  user_id?: string;
  email?: string;
  is_active?: boolean;
  is_phone_verified?: boolean;
  is_superuser?: boolean;
}

interface UserRateLimits {
  user?: UserInfo;
}

const RateLimitMonitoring: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [config, setConfig] = useState<RateLimitConfig | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [userIdInput, setUserIdInput] = useState<string>('');
  const [userRateLimits, setUserRateLimits] = useState<UserRateLimits | null>(null);

  const loadAllData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, configData, healthData] = await Promise.all([
        rateLimitMonitoringAPI.getStats(),
        rateLimitMonitoringAPI.getConfig(),
        rateLimitMonitoringAPI.getHealth()
      ]);

      // API may return data directly or wrapped in a data property
      const statsResponse = statsData.data as unknown as Stats & { data?: Stats };
      const configResponse = configData.data as unknown as RateLimitConfig & { data?: RateLimitConfig };
      const healthResponse = healthData.data as unknown as Health & { data?: Health };

      setStats(statsResponse.data || statsResponse);
      setConfig(configResponse.data || configResponse);
      setHealth(healthResponse.data || healthResponse);
      setLastUpdated(new Date());
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string | Array<{ msg?: string }> } } };
      const errorMessage = error.response?.data?.detail
        ? (Array.isArray(error.response.data.detail)
          ? error.response.data.detail.map((e) => e.msg).join(', ')
          : error.response.data.detail)
        : handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAllData, 30000);
    return () => clearInterval(interval);
  }, [loadAllData]);

  const handleLookupUser = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!userIdInput.trim()) return;

    try {
      const response = await rateLimitMonitoringAPI.getUserRateLimits(userIdInput.trim());
      const userResponse = response.data as unknown as UserRateLimits & { data?: UserRateLimits };
      setUserRateLimits(userResponse.data || userResponse);
    } catch (err) {
      alert('Грешка при търсенето: ' + handleApiError(err));
    }
  };

  const handleResetUser = async (userId: string): Promise<void> => {
    if (window.confirm('Сигурни ли сте, че искате да нулирате rate limits за този потребител?')) {
      try {
        await rateLimitMonitoringAPI.resetUserRateLimits(userId);
        alert('Rate limits успешно нулирани.');
        if (userIdInput.trim() === userId) {
          const fakeEvent = { preventDefault: () => {} } as FormEvent<HTMLFormElement>;
          handleLookupUser(fakeEvent);
        }
      } catch (err) {
        alert('Грешка при нулирането: ' + handleApiError(err));
      }
    }
  };

  const getHealthBadgeClass = (healthy: boolean | undefined): string => {
    return healthy ? 'badge-success' : 'badge-danger';
  };

  if (loading && !stats) {
    return (
      <div className="rate-limit-monitoring">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Зареждане на данни...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rate-limit-monitoring">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Грешка при зареждане на данните</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={loadAllData}>
            <i className="fas fa-redo"></i> Опитайте отново
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rate-limit-monitoring">
      <div className="monitoring-header">
        <div className="header-left">
          <h2><i className="fas fa-tachometer-alt"></i> Rate Limit Monitoring</h2>
          <p className="last-updated">
            Последна актуализация: {lastUpdated.toLocaleTimeString('bg-BG')}
          </p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadAllData} disabled={loading}>
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
            Обнови
          </button>
        </div>
      </div>

      {/* Health Status */}
      <div className="health-card">
        <h3><i className="fas fa-heartbeat"></i> Health Status</h3>
        <div className="health-details">
          <div className="health-item">
            <span>Overall Health:</span>
            <span className={`badge ${getHealthBadgeClass(health?.healthy)}`}>
              {health?.healthy ? 'Healthy' : 'Unhealthy'}
            </span>
          </div>
          <div className="health-item">
            <span>Redis Connected:</span>
            <span className={`badge ${getHealthBadgeClass(health?.details?.redis_connected)}`}>
              {health?.details?.redis_connected ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="health-item">
            <span>Redis Responsive:</span>
            <span className={`badge ${getHealthBadgeClass(health?.details?.redis_responsive)}`}>
              {health?.details?.redis_responsive ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="health-item">
            <span>Rate Limiting Active:</span>
            <span className={`badge ${getHealthBadgeClass(health?.details?.rate_limiting_active)}`}>
              {health?.details?.rate_limiting_active ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Configuration */}
      {config && (
        <div className="config-section">
          <h3><i className="fas fa-cog"></i> Rate Limit Configuration</h3>
          <div className="config-grid">
            <div className="config-group">
              <h4>Authentication Limits</h4>
              <div className="config-item">
                <span>Registration:</span>
                <span>{config.registration}</span>
              </div>
              <div className="config-item">
                <span>Login:</span>
                <span>{config.login}</span>
              </div>
              <div className="config-item">
                <span>Login Failed:</span>
                <span>{config.login_failed}</span>
              </div>
              <div className="config-item">
                <span>Verification:</span>
                <span>{config.verification}</span>
              </div>
              <div className="config-item">
                <span>Refresh Token:</span>
                <span>{config.refresh}</span>
              </div>
            </div>

            <div className="config-group">
              <h4>Account Limits</h4>
              <div className="config-item">
                <span>Password Change:</span>
                <span>{config.password_change}</span>
              </div>
              <div className="config-item">
                <span>Password Reset:</span>
                <span>{config.password_reset}</span>
              </div>
              <div className="config-item">
                <span>Current User:</span>
                <span>{config.current_user}</span>
              </div>
              <div className="config-item">
                <span>Logout:</span>
                <span>{config.logout}</span>
              </div>
            </div>

            <div className="config-group">
              <h4>Application Limits</h4>
              <div className="config-item">
                <span>Emergency:</span>
                <span>{config.emergency}</span>
              </div>
              <div className="config-item">
                <span>Location Update:</span>
                <span>{config.location_update}</span>
              </div>
              <div className="config-item">
                <span>Burst Multiplier:</span>
                <span>{config.burst_multiplier}x</span>
              </div>
              <div className="config-item">
                <span>Lockout Threshold:</span>
                <span>{config.lockout_threshold} attempts</span>
              </div>
              <div className="config-item">
                <span>Lockout Duration:</span>
                <span>{config.lockout_duration}s</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Lookup */}
      <div className="user-lookup-section">
        <h3><i className="fas fa-search"></i> User Rate Limit Lookup</h3>
        <form onSubmit={handleLookupUser} className="lookup-form">
          <input
            type="text"
            value={userIdInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setUserIdInput(e.target.value)}
            placeholder="Enter User ID (UUID)"
            className="lookup-input"
          />
          <button type="submit" className="btn btn-primary">
            <i className="fas fa-search"></i> Search
          </button>
        </form>

        {userRateLimits && (
          <div className="user-rate-limits">
            <div className="user-info">
              <h4>User Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <span>Email:</span>
                  <strong>{userRateLimits.user?.email}</strong>
                </div>
                <div className="info-item">
                  <span>Active:</span>
                  <strong>{userRateLimits.user?.is_active ? 'Yes' : 'No'}</strong>
                </div>
                <div className="info-item">
                  <span>Phone Verified:</span>
                  <strong>{userRateLimits.user?.is_phone_verified ? 'Yes' : 'No'}</strong>
                </div>
                <div className="info-item">
                  <span>Superuser:</span>
                  <strong>{userRateLimits.user?.is_superuser ? 'Yes' : 'No'}</strong>
                </div>
              </div>
            </div>

            <div className="rate-limit-actions">
              <button
                className="btn btn-warning"
                onClick={() => handleResetUser(userRateLimits.user?.user_id || '')}
              >
                <i className="fas fa-undo"></i> Reset Rate Limits
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RateLimitMonitoring;
